import type { AuditEntry, AuditListItem, NarratedChange } from '@clockwise/clients/audit';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

type Operation = AuditEntry['operation'];

// `whitespace-normal break-words` overrides the TableCell default
// (`whitespace-nowrap`) so a long value wraps within its fixed-width column.
// `align-top` keeps multi-line before/after rows aligned to the field label.
const cellClass = 'align-top whitespace-normal break-words text-xs';

/** The actor/impersonation fields an audit row needs to render "who". */
export type AuditActorFields = Pick<
  AuditListItem,
  'actorId' | 'actorLabel' | 'impersonatedUserId' | 'impersonatedUserLabel'
>;

// Same shade family (800 fg / 100 bg / dark:900-40) as the shared
// `LifecycleStatusBadge`'s "Active" pill, so "create" reads as the same
// green pill everywhere — just applied across all four operations for one
// consistent badge look.
const OPERATION_STYLES: Record<Operation, string> = {
  create: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  delete: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  restore: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
};

/** Colored pill for an audit operation (create / update / delete / restore). */
export function OperationBadge({ operation }: { operation: Operation }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        OPERATION_STYLES[operation],
      )}
    >
      {operation}
    </span>
  );
}

/**
 * Field-level before/after table for an expanded audit row. Pass `resolveLabel`
 * to translate field paths the narrator can't name on its own — notably
 * `customFields.<uuid>` keys on a single-object screen, which resolve to the
 * human label via the host's `useResolveCustomFieldLabel`.
 */
export function DiffTable({
  changes,
  resolveLabel,
  operation,
}: {
  changes: NarratedChange[];
  resolveLabel?: (field: string, fallback: string) => string;
  /** When given, a `create` collapses to a single "Value" column (before is
   *  always null) and a `delete` shows the pre-deletion "Value"; otherwise the
   *  full Before/After pair is shown. */
  operation?: Operation;
}) {
  if (changes.length === 0) {
    return (
      <p className="ml-7 mt-2 text-xs text-muted-foreground">No field-level changes recorded.</p>
    );
  }
  // A create has no meaningful "before" (always null) — collapse to one
  // "Value" column. NOT delete: soft-deletes here are status transitions
  // (active → inactive), so both sides carry real information.
  const singleSide = operation === 'create' ? 'after' : null;
  // Fixed layout: the Field column is pinned and the value column(s) split the
  // rest evenly. Without this a single dense value (e.g. a JSONB blob) forces
  // the column — and the whole page — to scroll horizontally. `cellClass` then
  // lets long values wrap instead of overriding the layout.
  return (
    <div className="ml-7 mt-2 overflow-hidden rounded-md border">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/3">Field</TableHead>
            {singleSide ? (
              <TableHead>Value</TableHead>
            ) : (
              <>
                <TableHead>Before</TableHead>
                <TableHead>After</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {changes.map((c) => (
            <TableRow key={c.field}>
              <TableCell className={cellClass}>
                {resolveLabel ? resolveLabel(c.field, c.label) : c.label}
              </TableCell>
              {singleSide ? (
                <TableCell className={cellClass}>{renderChangeValue(c, singleSide)}</TableCell>
              ) : (
                <>
                  <TableCell className={cellClass}>{renderChangeValue(c, 'before')}</TableCell>
                  <TableCell className={cellClass}>{renderChangeValue(c, 'after')}</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** "Dana Dev" / "Dana Dev (as Sam Support)" / "System" / "Unknown user". */
export function AuditActor({ entry }: { entry: AuditActorFields }) {
  const who = entry.actorLabel ?? (entry.actorId ? 'Unknown user' : 'System');
  return (
    <span>
      {who}
      {entry.impersonatedUserId ? (
        <span className="text-xs text-muted-foreground">
          {' '}
          (as {entry.impersonatedUserLabel ?? 'Unknown user'})
        </span>
      ) : null}
    </span>
  );
}

function renderChangeValue(change: NarratedChange, side: 'before' | 'after'): React.ReactNode {
  // Enum and reference fields carry a resolved human label alongside the raw
  // value; prefer it. References fall through to the raw id when resolution
  // missed (e.g. a deleted referent).
  if (change.display === 'enum' || change.display === 'reference') {
    const display = side === 'before' ? change.beforeDisplay : change.afterDisplay;
    if (display) return display;
  }
  return renderValue(side === 'before' ? change.before : change.after);
}

function renderValue(v: unknown): React.ReactNode {
  // A missing value (null/undefined → an unset column) renders as a blank cell
  // rather than the literal "null", which reads as developer jargon to the
  // people who actually read the audit log.
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v || <span className="text-muted-foreground">""</span>;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // A JSON blob is one long, space-free token; `break-all` lets it wrap inside
  // the fixed-width cell rather than overflowing it.
  return (
    <code className="whitespace-pre-wrap break-all font-mono text-xs">{JSON.stringify(v)}</code>
  );
}
